import { Capacitor, SystemBars, SystemBarsStyle } from "@capacitor/core";

export type CraftoffRuntime = "web" | "ios" | "android";
export type HapticCue = "select" | "place" | "success" | "warning";

const buildRuntime = typeof __CRAFTOFF_RUNTIME__ === "string" ? __CRAFTOFF_RUNTIME__ : "web";
export const nativeApiOrigin = typeof __CRAFTOFF_API_ORIGIN__ === "string"
  ? __CRAFTOFF_API_ORIGIN__
  : "";
export const isNativeBuild = buildRuntime === "native";
export const runtime: CraftoffRuntime = isNativeBuild
  ? Capacitor.getPlatform() === "ios" ? "ios" : "android"
  : "web";

export function apiUrl(path: string): string {
  if (!isNativeBuild) return path;
  return new URL(path, nativeApiOrigin).toString();
}

export function webSocketUrl(path: string, origin = nativeApiOrigin): string {
  if (!isNativeBuild) {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return new URL(path, `${protocol}//${window.location.host}`).toString();
  }
  const url = new URL(path, origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

export async function initializePlatform(): Promise<void> {
  document.documentElement.dataset.runtime = runtime;
  if (!isNativeBuild) return;

  const [{ App }, { Network }, { SplashScreen }, { StatusBar, Style }] = await Promise.all([
    import("@capacitor/app"),
    import("@capacitor/network"),
    import("@capacitor/splash-screen"),
    import("@capacitor/status-bar")
  ]);

  await StatusBar.setStyle({ style: Style.Dark }).catch(() => undefined);
  if (runtime === "android") {
    await SystemBars.setStyle({ style: SystemBarsStyle.Dark }).catch(() => undefined);
  }
  const network = await Network.getStatus();
  document.documentElement.dataset.network = network.connected ? "online" : "offline";
  await Network.addListener("networkStatusChange", (status) => {
    document.documentElement.dataset.network = status.connected ? "online" : "offline";
    window.dispatchEvent(new CustomEvent("craftoff:network", { detail: status }));
  });
  await App.addListener("appStateChange", ({ isActive }) => {
    document.documentElement.dataset.appState = isActive ? "active" : "background";
    window.dispatchEvent(new CustomEvent("craftoff:app-state", { detail: { isActive } }));
  });
  if (runtime === "android") {
    await App.addListener("backButton", () => {
      window.dispatchEvent(new CustomEvent("craftoff:back"));
    });
  }
  await SplashScreen.hide();
}

export function haptic(cue: HapticCue): void {
  if (!isNativeBuild) return;
  void import("@capacitor/haptics").then(({ Haptics, ImpactStyle, NotificationType }) => {
    if (cue === "success") return Haptics.notification({ type: NotificationType.Success });
    if (cue === "warning") return Haptics.notification({ type: NotificationType.Warning });
    return Haptics.impact({ style: cue === "place" ? ImpactStyle.Medium : ImpactStyle.Light });
  }).catch(() => undefined);
}
